import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { reportIssuePageFooter } from './layout/footerConfigs'

export const ReportIssuePage: React.FC = () => {
  const isMobile = useIsMobile()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    description: '',
    priority: 'normal',
    os: '',
    link: '',
    steps: ''
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const heroRef = React.useRef(null)
  const categoryRef = React.useRef(null)
  const formRef = React.useRef<HTMLElement>(null)
  const isHeroInView = useInView(heroRef, { once: false, amount: 0.3 })
  const isCategoryInView = useInView(categoryRef, { once: false, amount: 0.2 })

  // Scroll to form when category is selected
  React.useEffect(() => {
    if (selectedCategory && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [selectedCategory])

  // Detect prefers-reduced-motion
  const prefersReducedMotion = React.useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )



  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { isScrolled: _isScrolled, scrollProgress } = useOptimizedScroll()

  const categories = [
    {
      id: 'rare-earth',
      title: 'Rare Earth Services',
      description: 'Issues with rare earth permits and trading'
    },
    {
      id: 'verpackg',
      title: 'VerpackG / Compliance',
      description: 'Compliance deletion and regulatory matters'
    },
    {
      id: 'recommerce',
      title: 'ReCommerce / 5G Projects',
      description: '5G recommerce and trade intelligence issues'
    },
    {
      id: 'software',
      title: 'Software',
      description: 'Project Crimson, Project Velocity, Project Nexus',
      isBlurred: true
    },
    {
      id: 'hardware',
      title: 'Hardware',
      description: 'Project Quantum and related devices',
      isBlurred: true
    },
    {
      id: 'other',
      title: 'Other / General',
      description: 'General inquiries and other issues'
    }
  ]

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Get the API URL from environment or use default
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'

      // Get category title
      const categoryTitle = categories.find(c => c.id === selectedCategory)?.title || selectedCategory

      // Send form data to backend API
      const response = await fetch(`${apiUrl}/api/report-issue/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          category: categoryTitle
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setIsSubmitted(true)
      } else {
        throw new Error(result.error || 'Failed to submit issue report')
      }

    } catch (error) {
      console.error('Error submitting issue report:', error)
      setSubmitError('Something went wrong. Please try again or email us directly at support@mernaki.de')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}



      {/* Main Content Wrapper with Rounded Bottom Corners */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }
          }
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0],
              }
          }
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.15, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }
          }
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Hero Section */}
        <motion.section
          ref={heroRef}
          className="relative pt-48 pb-16"
          initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0 }}
          animate={isHeroInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight tracking-tight"
              style={{
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
              initial={prefersReducedMotion.current ? "visible" : "hidden"}
              animate={isHeroInView ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {'Report an Issue'.split('').map((char, index) => (
                <motion.span
                  key={`char-${index}`}
                  variants={{
                    hidden: { opacity: 0, x: -20, y: 10 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: [0.4, 0.0, 0.2, 1]
                      }
                    }
                  }}
                  style={{ display: 'inline-block', whiteSpace: char === ' ' ? 'pre' : 'normal' }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-4"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.4, 0.0, 0.2, 1] }}
            >
              Experiencing a problem with one of our services, software tools, or hardware products?
            </motion.p>
            <motion.p
              className="text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
              style={{
                color: '#999999',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.4, ease: [0.4, 0.0, 0.2, 1] }}
            >
              Submit the issue below and our team will assist you as soon as possible.
            </motion.p>
          </div>
        </motion.section>

        {/* Category Selector Section */}
        <section ref={categoryRef} className="relative pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0 }}
              animate={isCategoryInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
            >
              {categories.map((category, index) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className="text-left p-6 rounded-2xl transition-all duration-300"
                  style={{
                    backgroundColor: selectedCategory === category.id
                      ? 'rgba(74, 144, 226, 0.15)'
                      : 'rgba(255, 255, 255, 0.6)',
                    border: selectedCategory === category.id
                      ? '2px solid rgba(74, 144, 226, 0.5)'
                      : '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: selectedCategory === category.id
                      ? '0 8px 24px rgba(74, 144, 226, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    cursor: 'pointer',
                    backdropFilter: 'blur(10px)'
                  }}
                  initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                  animate={isCategoryInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                  whileHover={!prefersReducedMotion.current ? { y: -4, scale: 1.02 } : {}}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category.id) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category.id) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)'
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <h3
                    className="text-xl mb-2"
                    style={{
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 600
                    }}
                  >
                    {category.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{
                      color: '#666666',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      lineHeight: '1.5',
                      filter: category.isBlurred ? 'blur(3px)' : 'none',
                      userSelect: category.isBlurred ? 'none' : 'auto',
                      pointerEvents: category.isBlurred ? 'none' : 'auto'
                    }}
                  >
                    {category.description}
                  </p>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Issue Form Section */}
        {selectedCategory && !isSubmitted && (
          <motion.section
            ref={formRef}
            className="relative pb-24"
            initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Container 1: Personal Information */}
                  <motion.div
                    className="p-6 rounded-2xl"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                      backdropFilter: 'blur(10px)'
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                  >
                    <h3
                      className="text-lg mb-6"
                      style={{
                        color: '#1A1A1A',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 600
                      }}
                    >
                      Personal Information
                    </h3>
                    {/* Name */}
                    <div className="mb-5">
                      <label
                        htmlFor="name"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Your Name <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>

                    {/* Email */}
                    <div className="mb-5">
                      <label
                        htmlFor="email"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Email Address <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>

                    {/* Category (read-only) */}
                    <div>
                      <label
                        htmlFor="category"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Category <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="category"
                        value={categories.find(c => c.id === selectedCategory)?.title || ''}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl"
                        style={{
                          backgroundColor: 'rgba(74, 144, 226, 0.1)',
                          border: '1px solid rgba(74, 144, 226, 0.3)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Container 2: Issue Details */}
                  <motion.div
                    className="p-6 rounded-2xl"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                      backdropFilter: 'blur(10px)'
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                  >
                    <h3
                      className="text-lg mb-6"
                      style={{
                        color: '#1A1A1A',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 600
                      }}
                    >
                      Issue Details
                    </h3>

                    {/* Subject */}
                    <div className="mb-5">
                      <label
                        htmlFor="subject"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Subject <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Describe the issue <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows={8}
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200 resize-none"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl text-center mt-4"
                        style={{
                          backgroundColor: 'rgba(231, 76, 60, 0.1)',
                          border: '1px solid rgba(231, 76, 60, 0.3)',
                          color: '#E74C3C',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 500,
                          fontSize: '14px'
                        }}
                      >
                        ✗ {submitError}
                      </motion.div>
                    )}

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-2.5 px-5 rounded-lg transition-all duration-300 mt-6"
                      style={{
                        backgroundColor: isSubmitting ? 'rgba(74, 144, 226, 0.06)' : 'rgba(74, 144, 226, 0.12)',
                        color: isSubmitting ? 'rgba(74, 144, 226, 0.5)' : '#4A90E2',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        border: '1px solid rgba(74, 144, 226, 0.25)',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 1px 4px rgba(74, 144, 226, 0.1)',
                        letterSpacing: '0.01em',
                        opacity: isSubmitting ? 0.7 : 1
                      }}
                      initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
                      whileHover={!prefersReducedMotion.current && !isSubmitting ? { y: -1, scale: 1.005 } : {}}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = 'rgba(74, 144, 226, 0.2)'
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.4)'
                          e.currentTarget.style.boxShadow = '0 2px 12px rgba(74, 144, 226, 0.2)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = 'rgba(74, 144, 226, 0.12)'
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.25)'
                          e.currentTarget.style.boxShadow = '0 1px 4px rgba(74, 144, 226, 0.1)'
                        }
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Report'}
                    </motion.button>
                  </motion.div>

                  {/* Container 3: Priority & Additional Info */}
                  <motion.div
                    className="p-6 rounded-2xl"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                      backdropFilter: 'blur(10px)'
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
                  >
                    <h3
                      className="text-lg mb-6"
                      style={{
                        color: '#1A1A1A',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 600
                      }}
                    >
                      Priority & Details
                    </h3>

                    {/* Priority */}
                    <div className="mb-5">
                      <label
                        htmlFor="priority"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Priority <span style={{ color: '#E74C3C' }}>*</span>
                      </label>
                      <select
                        id="priority"
                        name="priority"
                        required
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="critical">Critical (system down)</option>
                      </select>
                    </div>

                    {/* Operating System */}
                    <div className="mb-5">
                      <label
                        htmlFor="os"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Operating System / Device
                      </label>
                      <select
                        id="os"
                        name="os"
                        value={formData.os}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <option value="">Select...</option>
                        <option value="macos">macOS</option>
                        <option value="ios">iOS</option>
                        <option value="windows">Windows</option>
                        <option value="linux">Linux</option>
                        <option value="macx-handheld">MacX Handheld</option>
                      </select>
                    </div>

                    {/* Link */}
                    <div className="mb-5">
                      <label
                        htmlFor="link"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Link where issue occurred
                      </label>
                      <input
                        type="url"
                        id="link"
                        name="link"
                        value={formData.link}
                        onChange={handleInputChange}
                        placeholder="https://..."
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>

                    {/* Steps to reproduce */}
                    <div>
                      <label
                        htmlFor="steps"
                        className="block text-sm mb-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 600
                        }}
                      >
                        Steps to reproduce
                      </label>
                      <textarea
                        id="steps"
                        name="steps"
                        rows={4}
                        value={formData.steps}
                        onChange={handleInputChange}
                        placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200 resize-none"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color: '#1A1A1A',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontSize: '15px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      />
                    </div>
                  </motion.div>

                </div>
              </form>
            </div>
          </motion.section>
        )}

        {/* Success State */}
        {isSubmitted && (
          <motion.section
            className="relative pb-24"
            initial={prefersReducedMotion.current ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div
                className="p-12 rounded-3xl"
                style={{
                  backgroundColor: 'rgba(46, 204, 113, 0.1)',
                  border: '2px solid rgba(46, 204, 113, 0.3)',
                  boxShadow: '0 8px 32px rgba(46, 204, 113, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <div className="text-6xl mb-6">✓</div>
                <h2
                  className="text-3xl mb-4"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  Thank you.
                </h2>
                <p
                  className="text-lg mb-2"
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500
                  }}
                >
                  Your issue has been submitted.
                </p>
                <p
                  className="text-base"
                  style={{
                    color: '#999999',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400
                  }}
                >
                  Our support team will contact you shortly.
                </p>
              </div>
            </div>
          </motion.section>
        )}

        {/* Emergency Support Notice */}
        <section className="relative pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="p-8 rounded-2xl text-center"
              style={{
                backgroundColor: 'rgba(231, 76, 60, 0.08)',
                border: '1px solid rgba(231, 76, 60, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <h3
                className="text-2xl mb-3"
                style={{
                  color: '#E74C3C',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600
                }}
              >
                Urgent issue?
              </h3>
              <p
                className="text-base mb-4"
                style={{
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  lineHeight: '1.6'
                }}
              >
                For time-sensitive matters, contact us directly:
              </p>
              <a
                href="mailto:support@mernaki.de"
                className="inline-block text-lg transition-all duration-300"
                style={{
                  color: '#E74C3C',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#C0392B'
                  e.currentTarget.style.textDecoration = 'underline'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#E74C3C'
                  e.currentTarget.style.textDecoration = 'none'
                }}
              >
                support@mernaki.de
              </a>
            </motion.div>
          </div>
        </section>

        {/* Helpful Links Section */}
        <section className="relative pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/help"
                className="p-6 rounded-xl text-center transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  textDecoration: 'none',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <h4
                  className="text-lg mb-2"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  Visit Help Center
                </h4>
                <p
                  className="text-sm"
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                  }}
                >
                  Browse FAQs and guides
                </p>
              </Link>

              <a
                href="#"
                className="p-6 rounded-xl text-center transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'not-allowed',
                  opacity: 0.6
                }}
              >
                <h4
                  className="text-lg mb-2"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  Read Documentation
                </h4>
                <p
                  className="text-sm"
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                  }}
                >
                  Technical documentation
                </p>
              </a>

              <a
                href="#"
                className="p-6 rounded-xl text-center transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'not-allowed',
                  opacity: 0.6
                }}
              >
                <h4
                  className="text-lg mb-2"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  View System Status
                </h4>
                <p
                  className="text-sm"
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                  }}
                >
                  Check service availability
                </p>
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* Footer - Conditional Mobile/Desktop */}
      {isMobile ? (
        <PageMobileFooter {...reportIssuePageFooter} />
      ) : (
        <footer
          className="relative"
          style={{
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '140px', // Extended padding to create background behind rounded corners
            paddingBottom: '40px',
            marginTop: '-80px' // Pull footer up to extend background behind page content
          }}
        >
          {/* Floating Gradient Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Main Footer Content - Compact Layout */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">

              {/* Left: Brand */}
              <div className="flex items-center gap-3">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <div>
                  <h3
                    className="text-lg"
                    style={{
                      color: '#FFFFFF',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontWeight: 400,
                      marginBottom: '4px'
                    }}
                  >
                    Mernaki
                  </h3>
                  <p
                    className="text-xs"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    Service • Software • Hardware
                  </p>
                </div>
              </div>

              {/* Center: Links */}
              <div className="flex items-center gap-6 flex-wrap justify-center">
                <Link
                  to="/"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Home
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/help"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Help Center
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/faq"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  FAQ
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/privacy-policy"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Privacy Policy
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/impressum"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Impressum
                </Link>
              </div>

              {/* Right: Contact */}
              <div className="text-center md:text-right">
                <p
                  className="text-sm mb-1"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400
                  }}
                >
                  Contact:
                </p>
                <a
                  href="mailto:support@mernaki.de"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                >
                  support@mernaki.de
                </a>
              </div>
            </div>

            {/* Bottom Row */}
            <div
              className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 300
                }}
              >
                Mernaki © {new Date().getFullYear()}
              </p>
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Engineered in Germany. Supported globally.
              </p>
            </div>
          </div>
        </footer>
      )}
    </>
  )
}
